import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/common/logo';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10">
          {/* Back Button */}
          <div className="mb-4">
            <Link
              to="/"
              className="inline-flex items-center text-primary hover:text-primary-dark font-medium transition-colors hover:underline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              Back to Home
            </Link>
          </div>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <Logo size="default" />
              <h1 className="text-2xl sm:text-3xl font-bold text-[#8B4513]">Pangolin Script</h1>
            </Link>
          </div>

          {/* Title */}
          <div className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-gray-600">Effective Date: January 2, 2026</p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Welcome to Pangolin ("App"). By using our App, you agree to these Terms of Service ("Terms"). If you do not agree, please do not use the App.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Use of the App</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Pangolin allows users to access publicly available YouTube content and generate scripts or summaries.</li>
              <li>You may use the App only for lawful purposes and in accordance with these Terms.</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. YouTube API Compliance</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Pangolin uses the YouTube API to access publicly available data.</li>
              <li>
                You agree to comply with all{' '}
                <a
                  href="https://www.youtube.com/t/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark hover:underline"
                >
                  YouTube API Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="https://www.youtube.com/howyoutubeworks/policies/community-guidelines/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark hover:underline"
                >
                  YouTube Community Guidelines
                </a>.
              </li>
              <li>You may only provide content that you own or have rights to access.</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Intellectual Property</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>All content generated by Pangolin is your responsibility.</li>
              <li>You retain rights to your original videos and content.</li>
              <li>Pangolin does not claim ownership of any user-provided content or publicly available YouTube data.</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Restrictions</h2>
            <p className="text-gray-700 mb-2">You may not:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Access private videos or data without permission</li>
              <li>Violate YouTube or Google policies using the App</li>
              <li>Use the App for illegal, harmful, or abusive purposes</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Disclaimer</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Pangolin is provided "as is" without warranties of any kind.</li>
              <li>We are not responsible for inaccuracies in YouTube data or scripts generated from the App.</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 6 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the maximum extent permitted by law, Pangolin is not liable for any damages arising from your use of the App, including lost data or profits.
            </p>

            <hr className="my-8 border-gray-200" />

            {/* Section 7 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Modifications</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>We may update these Terms at any time.</li>
              <li>Continued use of Pangolin constitutes acceptance of any revised Terms.</li>
            </ul>

            <hr className="my-8 border-gray-200" />

            {/* Section 8 */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact</h2>
            <p className="text-gray-700 mb-2">
              For questions regarding these Terms, please contact us at:
            </p>
            <p className="text-gray-700">
              Email:{' '}
              <a
                href="mailto:shahzaibfarooq582@gmail.com"
                className="text-primary hover:text-primary-dark hover:underline"
              >
                shahzaibfarooq582@gmail.com
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">© {new Date().getFullYear()} PangolinScript. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
